export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const delta = Date.now() - then;
  const minutes = Math.round(delta / 60_000);
  if (Math.abs(minutes) < 1) return 'just now';
  if (Math.abs(minutes) < 60) return `${Math.abs(minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return `${Math.abs(hours)} hr ago`;
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 45) return `${Math.abs(days)} days ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function clockTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function formatMoney(amount: number, currency = 'NGN'): string {
  try {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

export function formatMoneyCompact(amount: number, currency = 'NGN'): string {
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) return `${currency === 'NGN' ? '₦' : ''}${(amount / 1_000_000).toFixed(1)}M`;
  if (abs >= 1000) return `${currency === 'NGN' ? '₦' : ''}${(amount / 1000).toFixed(0)}k`;
  return formatMoney(amount, currency);
}

export function parseAmount(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

export function orgVisualStatus(org: {
  status: string;
  subscriptionStatus?: string | null;
}): string {
  if (org.status === 'ARCHIVED') return 'archived';
  if (org.status === 'SUSPENDED') return 'suspended';
  if (org.subscriptionStatus === 'TRIALING' || org.status === 'TRIAL') return 'trial';
  if (org.status === 'ACTIVE') return 'active';
  return org.status.toLowerCase();
}

/** Derived platform health when the org list has no health field. */
export function orgHealthStatus(status: string): string {
  if (status === 'SUSPENDED') return 'unavailable';
  if (status === 'ARCHIVED') return 'unknown';
  return 'healthy';
}

export function orgSlugHint(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

export function badgeStatus(raw: string | null | undefined): string {
  if (!raw) return 'unknown';
  const key = raw.toLowerCase().replaceAll(' ', '_');
  const map: Record<string, string> = {
    active: 'active',
    healthy: 'healthy',
    success: 'success',
    committed: 'success',
    connected: 'healthy',
    trial: 'trial',
    trialing: 'trial',
    degraded: 'degraded',
    warning: 'warning',
    pending: 'warning',
    pending_mapping: 'warning',
    pending_commit: 'warning',
    past_due: 'past_due',
    suspended: 'suspended',
    unavailable: 'unavailable',
    failed: 'failed',
    error: 'failed',
    failure: 'failed',
    denied: 'failed',
    archived: 'archived',
    cancelled: 'archived',
    canceled: 'archived',
    undone: 'unknown',
    unknown: 'unknown',
    unverified: 'unverified',
    disconnected: 'unknown',
    disabled: 'unknown',
    deprecated: 'unknown',
  };
  return map[key] ?? key;
}

export function formatDevice(userAgent: string | null | undefined): string {
  if (!userAgent) return 'Unknown device';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone|iPad/i.test(userAgent)) return 'iPhone or iPad';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'Mac';
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Linux/i.test(userAgent)) return 'Linux';
  return 'This browser';
}

export function greetingName(displayName: string): string {
  return displayName.split(/\s+/)[0] ?? displayName;
}

export function greetingPrefix(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
